import store from '@/store';
import { getRecorderEmails } from '@/constants';
import { GCAuth, GCAUTH_ACCESS_TOKEN_KEY } from '@/services/gcauth.service';
import { replaceRoute } from '@/utils/routerAccess';
import loggerService from '@/services/logger.service';
import { maskEmail } from '@goldcast/utils';
import { storeGCAuthTokens, trackMagicLinkVerification, startSessionValidationPolling } from '@/utils/magicLinkAuth';
import { redirectToOnDemandExperienceIfApplicable } from '@/utils/onDemandRedirect';

const notRegisteredCodes = ['EMAIL_NOT_FOUND', 'USER_REGISTRATION_ERROR', 'EMAIL_NOT_REGISTERED'];

export const AuthMixin = {
  store,
  computed: {
    eventIDToRedirectTo() {
      return this.$route.query.eventID;
    },
    eventLandingQueryParamsExist() {
      return this.eventIDToRedirectTo;
    },
    emailId() {
      return this.$route.query.email;
    },
    eventAndOrgQueryParamDetails() {
      if (this.eventLandingQueryParamsExist) {
        return {
          eventID: this.eventIDToRedirectTo
        };
      }
    }
  },
  beforeUnmount() {
    if (this.resendOtpTimer) {
      clearInterval(this.resendOtpTimer);
      this.resendOtpTimer = null;
    }
  },
  methods: {
    hasErrorCode(err, codes, patterns = []) {
      const text = [err?.message, err?.detail].join(' ');
      const regex = patterns.length ? new RegExp(patterns.join('|'), 'i') : null;
      return codes.includes(err?.apiErrorCode) || codes.includes(err?.error) || (regex && regex.test(text));
    },
    getLandingPageRouterParamsAfterLogin() {
      const canRedirectToEventRoute = this.eventLandingQueryParamsExist;
      const routeParams = {
        name: canRedirectToEventRoute ? 'EventSplash' : 'ErrorPage'
      };
      if (canRedirectToEventRoute) {
        routeParams['params'] = {
          eventID: this.eventIDToRedirectTo
        };
        routeParams['query'] = { postSignIn: '1' };
      }
      return routeParams;
    },
    processUserStatus: function (userStatus) {
      if (userStatus === 'FORCE_CHANGE_PASSWORD') {
        this.$router.push({
          name: 'confirmSignup',
          query: this.eventAndOrgQueryParamDetails
        });
      } else if (userStatus === 'CONFIRMED') {
        this.$router.push({
          name: 'login',
          query: this.eventAndOrgQueryParamDetails
        });
      } else if (userStatus === 'RESET_REQUIRED') {
        this.$router.push({
          name: 'forgotPasswordSubmit',
          query: this.eventAndOrgQueryParamDetails
        });
      }
      return;
    },
    checkStatus: async function (user) {
      if (user.challengeName === 'NEW_PASSWORD_REQUIRED') {
        this.$store.commit('setCognitoUserDetails', user);
        this.$router.replace({
          name: `external`
        });
        return;
      }
      await this.isFirstTime();
    },
    isFirstTime: async function () {
      const currentUserEmail = this.$store.getters.getCurrentUser?.email;
      const routeEmail = this.emailId;
      const emailToCheck = currentUserEmail || routeEmail;

      if (emailToCheck && getRecorderEmails().includes(emailToCheck)) {
        this.$router.push({
          name: 'RecorderLoginSuccess'
        });
        return;
      }
      const navigateTo = this.$route?.query?.navigate_to;
      if (navigateTo) {
        this.$router.push(navigateTo);
        return;
      }

      if (redirectToOnDemandExperienceIfApplicable({ store: this.$store, route: this.$route })) {
        return;
      }

      if (this.$store.state?.event?.useGCAuth) {
        startSessionValidationPolling(() => {
          loggerService.warn(
            'Auth: session validation detected invalid session - signing out and redirecting to /auth',
            {
              reason: 'session-validation-invalid',
              eventID: this.$store.state.event.event?.id,
              currentRoute: this.$route?.fullPath
            }
          );
          this.$store.dispatch('signUserOut');
          replaceRoute({
            path: '/auth',
            query: {
              eventID: this.$store.state.event.event.id
            }
          }).then(() => {
            loggerService.info(
              '[PAGE_RELOAD] Reloading after GCAuth session validation failure + sign-out redirect to /auth'
            );
            window.location.reload();
          });
        });
      }

      const landingRouteParams = this.getLandingPageRouterParamsAfterLogin();
      loggerService.info('Auth: redirecting to landing page after sign-in (sets postSignIn=1)', {
        landingRouteParams,
        triggeredFromRouteName: this.$route?.name,
        triggeredFromPath: this.$route?.fullPath
      });
      this.$router.replace(landingRouteParams);
    },
    stopLoading() {
      this.loading = false;
      this.loadingText = '';
    },
    handleMagicLinkInUse(err) {
      if (err?.statusCode !== 409 || err?.error !== 'session_exists') {
        return false;
      }

      const incomingUserId = this.$route?.params?.email;
      const incomingEventId = this.$route?.query?.eventID;

      let storedUserId = null;
      let storedEventId = null;
      try {
        const token = localStorage.getItem(GCAUTH_ACCESS_TOKEN_KEY);
        if (token) {
          const decoded = JSON.parse(atob(token.split('.')[1]));
          storedUserId = decoded.user_id;
          storedEventId = decoded.gc_event_id;
        }
      } catch (err) {
        loggerService.debug('Token decode failed, falling through to OTP', err);
      }

      // Same user reopening same event's magic link - bypass OTP
      const isSameUser = storedUserId && incomingUserId && storedUserId === incomingUserId;
      const isSameEvent = storedEventId && incomingEventId && storedEventId === incomingEventId;

      if (isSameUser && isSameEvent) {
        this.$store.dispatch('fetchCurrentUser').then(() => {
          this.isFirstTime();
        });
        return true;
      }

      const email = err?.details?.email;
      if (email?.includes('@')) {
        this.emailId = email;
        this.maskedEmail = maskEmail(email);
      }

      this.magicLinkInUse = true;
      this.stopLoading();
      return true;
    },
    async requestOTP() {
      try {
        this.loading = true;
        this.loadingText = this.$t('@@translate@@ Sending OTP') + '...';
        this.otpError = null;

        const emailForOTP = this.emailId;

        if (!emailForOTP || !emailForOTP.includes('@')) {
          this.otpError = this.$t(
            '@@translate@@ Email address is required to send OTP. Please use "Not your link?" to enter your email.'
          );
          this.stopLoading();
          return;
        }

        const response = await GCAuth.requestOTP(emailForOTP, this.eventId, this.shortId, this.secretCode);

        this.otpId = response.otp_id;
        this.otpSent = true;
        this.stopLoading();
        this.startResendCountdown();
      } catch (err) {
        loggerService.error('Failed to request OTP', err);
        if (err?.error === 'invalid_event_short_id') {
          this.otpError = this.$t('@@translate@@ Invalid event short ID. Please check the magic link URL.');
        } else if (err?.error === 'invalid_request' || err?.error === 'invalid_payload') {
          this.otpError =
            err?.details?.message ||
            err?.message ||
            this.$t('@@translate@@ Invalid request. Please ensure you are using a registered email for this event.');
        } else {
          this.otpError = err?.message || err?.error || this.$t('@@translate@@ Failed to send OTP. Please try again.');
        }
        this.stopLoading();
      }
    },
    async verifyOTP(otp, isEmbedded = false) {
      if (!otp || otp.length !== 6) {
        this.otpError = this.$t('@@translate@@ Please enter a valid 6-digit OTP.');
        return;
      }

      try {
        this.loading = true;
        this.loadingText = this.$t('@@translate@@ Verifying OTP') + '...';
        this.otpError = null;

        const result = await GCAuth.verifyOTP(otp, this.otpId);

        this.$store.commit('session/setOtpVerificationSuccess', true);
        storeGCAuthTokens(
          this.$store,
          result.signInUserSession.accessToken.jwtToken,
          result.signInUserSession.refreshToken.token
        );
        // OTP verification can swap identity (session_exists flow), so the cached
        // currentUser from the previous session must not be served here.
        const currentUser = await this.$store.dispatch('fetchCurrentUser', { force: true });

        trackMagicLinkVerification({
          email: this.emailId,
          eventId: this.eventId,
          secretCode: this.secretCode,
          userId: currentUser?.id || this.$store.getters.getCurrentUser?.id,
          isEmbedded
        });

        await this.isFirstTime();
      } catch (err) {
        loggerService.error('Failed to verify OTP', err);

        let errorMessage = err?.message || err?.error || this.$t('@@translate@@ Invalid OTP. Please try again.');

        if (err?.error === 'invalid_otp') {
          errorMessage = this.$t(
            '@@translate@@ The OTP you entered is incorrect. Please double-check the code and try again.'
          );
        } else if (err?.message?.includes('expired') || err?.message?.includes('revoked')) {
          errorMessage = this.$t('@@translate@@ OTP has expired or is invalid. Please request a new OTP.');
          this.otpSent = false;
          this.otpId = null;
          this.canResendOtp = true;
          this.resendCountdown = 0;
        }

        this.otpError = errorMessage;
        this.stopLoading();
      }
    },
    async resendOTP() {
      if (this.canResendOtp) {
        await this.requestOTP();
      }
    },
    notYourLink() {
      this.showNotYourLink = true;
      this.notYourLinkError = null;
    },
    backToOtp() {
      this.showNotYourLink = false;
      this.notYourLinkEmail = '';
      this.notYourLinkError = null;
    },
    backFromNotYourLinkSuccess() {
      this.notYourLinkSuccessMaskedEmail = null;
    },
    async requestNewLink(email) {
      if (!email || !email.includes('@')) {
        this.notYourLinkError = this.$t('@@translate@@ Please enter a valid email address.');
        return;
      }

      try {
        this.loading = true;
        this.loadingText = this.$t('@@translate@@ Sending magic link') + '...';
        this.notYourLinkError = null;

        await this.$store.dispatch('session/resendMagicLink', {
          email,
          eventId: this.eventId
        });

        this.stopLoading();
        this.settingAuthEmail(email);
        this.notYourLinkError = null;
        this.notYourLinkSuccessMaskedEmail = maskEmail(email);
      } catch (err) {
        loggerService.error('Failed to request new magic link', err);

        if (this.hasErrorCode(err, notRegisteredCodes, ['not registered', 'email not found'])) {
          this.notYourLinkError = this.$t(
            '@@translate@@ This email is not registered for this event. To register, complete the form below.'
          );
        } else {
          this.notYourLinkError =
            err?.message || err?.detail || this.$t('@@translate@@ Failed to send magic link. Please try again.');
        }
        this.stopLoading();
      }
    },
    startResendCountdown() {
      clearInterval(this.resendOtpTimer);
      this.canResendOtp = false;
      this.resendCountdown = 120;

      this.resendOtpTimer = setInterval(() => {
        if (--this.resendCountdown <= 0) {
          this.canResendOtp = true;
          clearInterval(this.resendOtpTimer);
          this.resendOtpTimer = null;
        }
      }, 1000);
    }
  }
};
